import React from "react";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../types";
import CollectionList, {
  CollectionListSpec,
  useCollectionItems,
} from "../components/CollectionList";
import { useProject } from "../data/useProject";
import { sheetSubjectCollection } from "../data/sheet";
import {
  CollectionReference,
  DocumentReference,
  DocumentSnapshot,
  FieldValue,
  Query,
  QueryDocumentSnapshot,
} from "@atgof-firebase/firebase";
import { UserContext } from "../data/userContext";
import { useNavigation } from "@react-navigation/native";
import { applicandTitle } from "./ApplicandScreen";
import { LanguageContext } from "../common/language";
import { Phrase, PhraseKey } from "../common/phrases";
import ListItem from "../components/ListItem";
import NewSheetOrApplicandButton from "../components/NewSheetOrApplicandButton";
import SectionHeader from "../components/SectionHeader";
import NavPage from "../components/pages/NavPage";
import { comparePotentiallyNumbered, episodeLabel } from "../common/util";
import { notDeleted } from "../common/general";
import { Text } from "react-native-paper";
import { ProjectContext } from "../data/projectContext";
import {
  isCharacterCategory,
  SheetCategory,
  SheetCharacterCategory,
} from "../common/sheet";
import { SubjectSpec, hasSubject, idForSubjectName } from "../common/subject";
import { sceneLabel } from "@atgof-common/scene";
import { View } from "react-native";
import {
  APPLICAND_KINDS,
  applicandCollectionName,
  ApplicandKind,
  kindOfApplicand,
  userFacingApplicandKind,
} from "@atgof-common/applicand";
import { ExportButton, pdfExportPhrases } from "../components/ExportButton";
import { useSeasons } from "../data/useSeasons";
import { useDocument } from "../data/useDocument";
import { compareEpisodes } from "@atgof-common/episode";

type ApplicandData = {
  path: string;
  title: string;
  description: string | undefined;
  sceneLabels: string[];
};

function applicandData(
  ph: (k: PhraseKey) => Phrase,
  applicand: DocumentSnapshot,
  subjectIsCharacter: boolean,
): ApplicandData {
  return {
    path: applicand.ref.path,
    title: applicandTitle(ph, applicand, subjectIsCharacter),
    description: applicand.get("description"),
    sceneLabels: (applicand.get("scenes") ?? []).map((ref: DocumentReference) =>
      sceneLabel(ref.path),
    ),
  };
}

type EpisodeData = { path: string; title: string };

function episodeData(
  ph: (k: PhraseKey) => Phrase,
  episode: DocumentSnapshot,
  daysRatherThanEpisodes: boolean,
): EpisodeData {
  const path = episode.ref.path;
  return {
    path,
    title: episodeLabel(ph, path, false, daysRatherThanEpisodes),
  };
}

export default function SheetSubjectDetailScreen({
  route,
}: NativeStackScreenProps<RootStackParamList, "sheetSubjectDetail">) {
  const { subjectKind, subjectName, performerKind } = route.params;
  const subjectIsCharacter = subjectKind === "character";
  const project = useProject();
  const daysRatherThanEpisodes: boolean =
    useDocument(project)?.get("daysRatherThanEpisodes") ?? false;
  const { categories, adminCategories } = React.useContext(ProjectContext);
  const { user } = React.useContext(UserContext);
  const { ph } = React.useContext(LanguageContext);
  const { navigate } = useNavigation();
  const seasonsCollSpec = React.useMemo(
    () =>
      project
        ? {
            collection: project.collection("seasons"),
            addConstraints: (q: Query) => q.orderBy("wrapDate", "desc"),
            process: (items: DocumentSnapshot[]) =>
              items.map((item) => item.id),
          }
        : undefined,
    [project],
  );
  const seasonIds = useCollectionItems<string>(seasonsCollSpec);
  const [collections, setCollections] = React.useState<
    CollectionListSpec<any, any>[] | undefined
  >();
  const [headerRight, setHeaderRight] = React.useState<React.ReactNode>();
  const seasons = useSeasons(false);
  React.useEffect(() => {
    if (!project) {
      setCollections(undefined);
      return;
    }
    const subjDoc = sheetSubjectCollection(project, subjectKind).doc(
      idForSubjectName(subjectName),
    );

    setHeaderRight(
      subjectIsCharacter && adminCategories.length && seasons?.length ? (
        <ExportButton
          actions={[
            {
              kind: "sheetSubject",
              exportData: {
                season: seasons[0].ref,
                categories: adminCategories.filter(
                  isCharacterCategory,
                ) as SheetCharacterCategory[],
                subjectKind,
                subjects: [subjDoc],
              },
              phrases: pdfExportPhrases,
            },
          ]}
        />
      ) : undefined,
    );

    const subject: SubjectSpec = {
      subjectKind,
      subjectName,
      performerKind,
    };
    function newApplicandAction(
      collection: CollectionReference,
      category: SheetCategory,
    ) {
      return () => {
        const newDoc = collection.doc();
        const applicandKind = kindOfApplicand(newDoc);
        newDoc.set({
          createdBy: user.ref,
          createdAt: FieldValue.serverTimestamp(),
          lastModifiedBy: user.ref,
          lastModifiedAt: FieldValue.serverTimestamp(),
          order: -1, // TODO
          deleted: false,
          scenes: [],
          category,
          ...(applicandKind === "look" && subjectIsCharacter
            ? { look: "day" }
            : {}),
        });
        navigate("applicand", {
          isNew: "true",
          path: newDoc.path,
          subjectKind,
          subjectName,
        });
      };
    }
    function collListSpec(
      applicandKind: ApplicandKind,
      collection: CollectionReference,
    ): CollectionListSpec<ApplicandData, any> {
      return {
        collection,
        sortComparison: (a, b) => comparePotentiallyNumbered(a.title, b.title),
        addConstraints: (q) =>
          notDeleted(q.where("category", "in", categories)),
        process: (items: QueryDocumentSnapshot[]) =>
          items.map((item) => applicandData(ph, item, subjectIsCharacter)),
        renderHeader: () => (
          <SectionHeader
            title={
              ph(
                `${userFacingApplicandKind(applicandKind, subjectIsCharacter)}s`,
              ) as string
            }
          >
            <NewSheetOrApplicandButton
              kind={applicandKind}
              restrictToCategories={subjectIsCharacter ? undefined : ["arts"]}
              actionForCategory={(category) =>
                newApplicandAction(collection, category)
              }
            />
          </SectionHeader>
        ),
        renderItem: ({ item }: { item: ApplicandData }) => (
          <ListItem
            label={
              <View
                style={{ flexDirection: "row", gap: 8, alignItems: "baseline" }}
              >
                <Text variant="labelLarge">{item.title}</Text>
                <Text variant="labelMedium">{item.description}</Text>
              </View>
            }
            onPress={() =>
              navigate("applicand", {
                path: item.path,
                subjectKind,
                subjectName,
              })
            }
          >
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                marginLeft: 4,
                marginTop: 4,
                gap: 10,
              }}
            >
              {item.sceneLabels.map((label) => (
                <Text key={label}>{label}</Text>
              ))}
            </View>
          </ListItem>
        ),
        searchTokens: (item: ApplicandData) => [
          item.title,
          item.description,
          ...item.sceneLabels,
        ],
      };
    }
    setCollections([
      ...APPLICAND_KINDS.map((applicandKind) =>
        collListSpec(
          applicandKind,
          subjDoc.collection(applicandCollectionName(applicandKind)),
        ),
      ),
      ...(project
        ? (seasonIds || []).map((seasonId) => ({
            collection: project
              .collection("seasons")
              .doc(seasonId)
              .collection("episodes"),
            addConstraints: (q: Query) => hasSubject(notDeleted(q), subject),
            process: (items: QueryDocumentSnapshot[]) =>
              (daysRatherThanEpisodes
                ? items.sort(compareEpisodes)
                : items.sort(compareEpisodes).reverse()
              ).map((item) => episodeData(ph, item, daysRatherThanEpisodes)),
            renderHeader: () => (
              <SectionHeader
                title={
                  daysRatherThanEpisodes
                    ? (ph("story-days") as string)
                    : ph("season") + " " + seasonId
                }
              ></SectionHeader>
            ),
            renderItem: ({ item }: { item: EpisodeData }) => (
              <ListItem
                label={item.title}
                onPress={() => {
                  navigate("episodeBreakdown", {
                    path: item.path,
                    subjectKind,
                    subjectName,
                    performerKind,
                  });
                }}
              />
            ),
            searchTokens: (item: EpisodeData) => [
              item.title,
              ...item.path.split("/").reverse().slice(0, 1), // TODO Do this properly
            ],
          }))
        : []),
    ]);
  }, [
    subjectKind,
    subjectName,
    project?.path,
    user.id,
    categories,
    adminCategories,
    ph,
    daysRatherThanEpisodes,
    seasonIds,
    seasons?.length ? seasons[0].ref.path : undefined,
  ]);
  return (
    <NavPage
      fullScreen
      headerRight={headerRight}
      isLoading={!collections}
      newSheetContext={{
        subject: { subjectName: subjectName, subjectKind: subjectKind },
      }}
    >
      {collections && (
        <CollectionList collections={collections} estimatedItemSize={85} />
      )}
    </NavPage>
  );
}
